import React, { FC } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import DonutChart from '../../../components/DonutChart/DonutChart';
import { IDashboardData } from '../../../services/dashboard';
import {
  CardTitle,
  ChartsRow,
  DonutCard,
  LegendCard,
  LegendDot,
  LegendItem,
  LegendName,
  LegendStatLine,
  LegendTextGroup,
  OverviewCont,
  TimelineCard,
} from './Overview.styles';

interface IOverview {
  data: IDashboardData;
}

const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const Overview: FC<IOverview> = ({ data }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const CHART_COLORS = [
    theme.colors.secondary[100],
    theme.colors.primary[100],
    theme.colors.primary[70],
  ];

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const dayKey = DAY_KEYS[date.getDay()];
    const translatedDay = t(`viewSurvey.dashboard.overview.days.${dayKey}`);
    const num = String(date.getDate()).padStart(2, '0');
    return `${translatedDay} ${num}`;
  };

  const { responseRate, completionTimeline } = data;
  const { valueCounts } = responseRate;

  const total = valueCounts.reduce((sum, { count }) => sum + count, 0);

  const pieData = valueCounts.map(({ value, count }) => ({
    name: value,
    value: count,
  }));

  const completedCount = valueCounts[0]?.count ?? 0;
  const completedPercent = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  const timelineData = completionTimeline.map(([dateStr, count]) => ({
    date: formatDate(dateStr),
    count,
  }));

  return (
    <OverviewCont>
      <ChartsRow>
        <DonutCard>
          <DonutChart
            colors={CHART_COLORS}
            count={completedCount}
            data={pieData}
            dataKey="value"
            share={completedPercent}
            height={220}
            width={220}
            innerRadius={72}
            outerRadius={104}
          />
        </DonutCard>

        <LegendCard>
          <CardTitle>{t('viewSurvey.dashboard.overview.responseRate')}</CardTitle>
          {valueCounts.map(({ value, count }, index) => {
            const percent = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <LegendItem key={value}>
                <LegendDot color={CHART_COLORS[index % CHART_COLORS.length]} />
                <LegendTextGroup>
                  <LegendStatLine>
                    {percent}% ({count})
                  </LegendStatLine>
                  <LegendName>{value}</LegendName>
                </LegendTextGroup>
              </LegendItem>
            );
          })}
        </LegendCard>
      </ChartsRow>

      <TimelineCard>
        <CardTitle>{t('viewSurvey.dashboard.overview.responsesPerDay')}</CardTitle>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={timelineData} margin={{ top: 8, right: 16, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="responseGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.colors.primary[100]} stopOpacity={0.15} />
                <stop offset="95%" stopColor={theme.colors.primary[100]} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              horizontal
              vertical={false}
              stroke={theme.colors.neutral.primary[10]}
              strokeDasharray="4 4"
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: theme.colors.neutral.primary[50] }}
              interval="preserveStartEnd"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: theme.colors.neutral.primary[50] }}
              allowDecimals={false}
              width={40}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: `1px solid ${theme.colors.neutral.primary[10]}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: 13,
              }}
              itemStyle={{ color: theme.colors.secondary[100] }}
              labelStyle={{ color: theme.colors.neutral.primary[60], marginBottom: 4 }}
            />
            <Area
              dataKey="count"
              name={t('viewSurvey.dashboard.overview.count')}
              type="monotone"
              stroke={theme.colors.primary[100]}
              strokeWidth={2}
              fill="url(#responseGradient)"
              dot={{ fill: theme.colors.primary[100], r: 3, strokeWidth: 0 }}
              activeDot={{ r: 5, fill: theme.colors.secondary[100], strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </TimelineCard>
    </OverviewCont>
  );
};

export default Overview;
